import { ReactElement, useState } from 'react';
import { useInView } from 'react-cool-inview';

import { useTheme, useMediaQuery, Box, Typography } from '@mui/material';

import axios from 'axios';
import { useEffectOnce } from 'usehooks-ts';

import Actions from './components/Actions';
import GameModal from './components/GameModal';
import useStyles from './styles';
import { GameProps } from './types';

import 'imagehover.css/css/imagehover.min.css';

import {
  useBalanceMutation,
  useBoolean,
  useSelector,
  useSessionMutation
} from '../../../../../../common/hooks';
import { Success } from '../../../../../../common/hooks/useSigninMutation';
import Iframe from '../../../../../../components/Iframe';
import Image from '../../../../../../components/Image';
import { Mode } from '../../../../types';

export default function Game({ game }: GameProps): ReactElement {
  const source = axios.CancelToken.source();

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const style = useStyles();

  const user = useSelector((state) => state.user);

  const { observe, inView } = useInView({
    rootMargin: '-50% 0px'
  });

  const [url, setUrl] = useState<string>('');

  const [modalOpen, setModalOpen] = useBoolean();
  const [realPlay, setRealPlay] = useBoolean();

  const [loginOpen, setLoginOpen] = useBoolean();

  const sessionMutation = useSessionMutation({
    options: {
      onSuccess: () => {
        handleRealButtonClick();
      },
      onError: () => {
        setLoginOpen.on();
      }
    }
  });

  const balanceMutation = useBalanceMutation({});

  const handleActionClick = (mode: Mode): void => {
    switch (mode) {
      case 'fun': {
        setUrl(game.method(game.urlData));
        setModalOpen.on();
        return;
      }
      case 'real': {
        sessionMutation.mutate({ sessionKey: user.data.sessionId });
        return;
      }
      default:
        return;
    }
  };

  const handleRealButtonClick = (values?: Success): void => {
    setUrl(game.method(game.urlData, user.data.loggedIn ? user.data.sessionId : values?.sessionId));

    setRealPlay.on();
    setModalOpen.on();
  };

  const handleCloseModal = (): void => {
    setModalOpen.off();
    if (realPlay) {
      balanceMutation.mutate({
        sessionKey: user.data.sessionId,
        playerId: user.data.playerId,
        email: user.data.email
      });
    }
  };

  useEffectOnce(() => {
    return () => source.cancel();
  });

  return (
    <>
      <Box ref={observe} sx={{ ...style.game }}>
        <Box component='figure' className='imghvr-fade' sx={{ ...style.figure }}>
          <Image
            alt={`${game.name} Poster`}
            thumbnailSrc={game.src.thumbnail}
            fullSrc={game.src.full}
          />

          {isXs ? (
            <figcaption className='ih-fade-up' style={{ opacity: inView ? 1 : 0 }}>
              <Actions
                loginOpen={loginOpen}
                hasFunMode={game.modes.includes('fun')}
                hasRealMode={game.modes.includes('real')}
                loading={sessionMutation.isLoading}
                onClick={handleActionClick}
                onSuccess={handleRealButtonClick}
                onSetModal={(bool) => (bool ? setLoginOpen.on() : setLoginOpen.off())}
              />
            </figcaption>
          ) : (
            <figcaption className='ih-fade-up'>
              <Actions
                loginOpen={loginOpen}
                hasFunMode={game.modes.includes('fun')}
                hasRealMode={game.modes.includes('real')}
                loading={sessionMutation.isLoading}
                onClick={handleActionClick}
                onSuccess={handleRealButtonClick}
                onSetModal={(bool) => (bool ? setLoginOpen.on() : setLoginOpen.off())}
              />
            </figcaption>
          )}
        </Box>

        <Typography align='center' color='textPrimary' fontWeight={600} variant='h6'>
          {game.name}
        </Typography>
      </Box>

      <GameModal open={modalOpen} onClose={handleCloseModal} title={game.name}>
        <Iframe title={game.name} src={url} />
      </GameModal>
    </>
  );
}
